/*
 * YumeFeel Sprint 1: estimated arrival and trial dates from #yf-landing-config (warehouse time zone, business days,
 * closed days, optional cut-off; no cut-off = next business day). Fills [data-yf-date="arrive|trial|trial-range|
 * orderby"] (optional data-yf-tpl with {range}); the server text is the fallback. QA: ?yf_now=2026-09-30T10:00.
 */
const cfgEl = document.getElementById("yf-landing-config");
let CFG = {};
try { CFG = JSON.parse(cfgEl ? cfgEl.textContent : "{}") || {}; } catch (e) { CFG = {}; }
const D = CFG.dates || {};
const DAY = 86400000;
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const closed = new Set((D.closed || []).map((s) => String(s).trim()).filter(Boolean));

function iso(d) { return d.toISOString().slice(0, 10); }
function civil(y, m, d) { return new Date(Date.UTC(y, m - 1, d, 12)); }
function add(d, n) { return new Date(d.getTime() + n * DAY); }
function business(d) { const w = d.getUTCDay(); return w > 0 && w < 6 && !closed.has(iso(d)); }
function nextBusiness(d) { let x = add(d, 1); while (!business(x)) x = add(x, 1); return x; }
function addBusiness(d, n) { let x = d; for (let i = 0; i < n; i++) x = nextBusiness(x); return x; }

function clock() {
  let now = new Date();
  try {
    const fake = new URLSearchParams(location.search).get("yf_now");
    if (fake && /^\d{4}-\d{2}-\d{2}/.test(fake)) {
      const [datePart, timePart = "10:00"] = fake.split("T");
      const [y, m, d] = datePart.split("-").map(Number);
      const [hh, mm] = timePart.split(":").map(Number);
      return { day: civil(y, m, d), minutes: (hh || 0) * 60 + (mm || 0) };
    }
  } catch (e) {}
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: D.tz || "America/Los_Angeles", year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hourCycle: "h23",
    }).formatToParts(now);
    const p = {};
    parts.forEach((x) => { p[x.type] = x.value; });
    return { day: civil(+p.year, +p.month, +p.day), minutes: (+p.hour % 24) * 60 + +p.minute };
  } catch (e) {
    return { day: civil(now.getFullYear(), now.getMonth() + 1, now.getDate()), minutes: now.getHours() * 60 + now.getMinutes() };
  }
}

export function compute() {
  if (!D.on) return null;
  const country = String(CFG.country || "").toUpperCase();
  if ((D.countries || ["US"]).indexOf(country) === -1) return null;
  const c = clock();
  let cutoff = null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(D.cutoff || "").trim());
  if (m) cutoff = +m[1] * 60 + +m[2];
  const R = business(c.day) && cutoff != null && c.minutes < cutoff ? c.day : nextBusiness(c.day);
  const dispatch = Math.max(1, +D.dispatch || 2);
  const shipBy = addBusiness(R, dispatch - 1);
  const from = addBusiness(R, Math.max(0, +D.tmin || 3));
  const to = addBusiness(shipBy, Math.max(0, +D.tmax || 5));
  const nights = +D.trial || 60;
  const out = { today: c.day, received: R, shipBy, from, to, trialFrom: add(from, nights), trialTo: add(to, nights) };
  const ob = D.orderby || {};
  if (ob.on && ob.from && ob.date) {
    const t = iso(c.day);
    if (t >= ob.from && t <= ob.date) out.orderby = { date: parse(ob.date), arrive: parse(ob.arrive), after: false };
    else if (ob.arrive && t > ob.date && t <= ob.arrive) out.orderby = { date: parse(ob.date), arrive: parse(ob.arrive), after: true };
  }
  return out;
}

function parse(s) { const [y, m, d] = String(s || "").split("-").map(Number); return y ? civil(y, m, d) : null; }
export function day(d) { return `${WK[d.getUTCDay()]}, ${MON[d.getUTCMonth()]} ${d.getUTCDate()}`; }
export function short(d) { return `${MON[d.getUTCMonth()]} ${d.getUTCDate()}`; }
export function range(a, b) {
  if (iso(a) === iso(b)) return short(a);
  if (a.getUTCMonth() === b.getUTCMonth() && a.getUTCFullYear() === b.getUTCFullYear()) return `${short(a)} to ${b.getUTCDate()}`;
  return `${short(a)} to ${short(b)}`;
}

function esc(s) { return String(s).replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[ch])); }
function strong(s) { return `<span class="yf-date" style="white-space:nowrap">${esc(s)}</span>`; }
// A date range never breaks in the middle (it wraps as one unit after its label).
function keep(html) { return `<span style="white-space:nowrap">${html}</span>`; }

let result;
let tracked = false;
function fill(el) {
  if (el.hasAttribute("data-yf-date-done")) return;
  el.setAttribute("data-yf-date-done", "");
  if (result === undefined) result = compute();
  const r = result;
  const kind = el.getAttribute("data-yf-date");
  const tpl = el.getAttribute("data-yf-tpl");
  if (!r) return;
  let html = "";
  if (kind === "arrive") {
    const txt = keep(`${strong(day(r.from))} \u2013 ${strong(day(r.to))}`);
    html = tpl ? esc(tpl).replace("{range}", txt) : `Estimated arrival: ${txt}`;
    html += `<span class="yf-srt"> (estimated from our ${+D.dispatch || 2} business day dispatch and ${+D.tmin || 3} to ${+D.tmax || 5} business day delivery)</span>`;
  } else if (kind === "trial" || kind === "trial-range") {
    const txt = range(r.trialFrom, r.trialTo);
    html = tpl ? esc(tpl).replace("{range}", strong(txt)) : kind === "trial" ? `Yours to try until about ${strong(txt)}.` : strong(txt);
  } else if (kind === "orderby") {
    if (!r.orderby || !r.orderby.date) return;
    if (r.orderby.after) {
      if (!el.getAttribute("data-after")) return;
      html = esc(el.getAttribute("data-after"));
    } else {
      html = esc(`Order by ${day(r.orderby.date)} for delivery by ${short(r.orderby.arrive || r.orderby.date)} (contiguous US).`);
    }
  }
  if (!html) return;
  el.innerHTML = html;
  el.hidden = false;
  const row = el.closest("[data-yf-date-row]");
  if (row) row.hidden = false;
  el.removeAttribute("aria-hidden");
  el.classList.add("is-dated");
  if (!tracked && (kind === "arrive" || kind === "trial") && typeof window.yfTrack === "function") {
    tracked = true;
    window.yfTrack("yf_dates_shown", { arrive_from: iso(r.from), arrive_to: iso(r.to) });
  }
}

function scan(root) {
  (root || document).querySelectorAll("[data-yf-date]:not([data-yf-date-done])").forEach(fill);
}

window.YfDates = { compute: () => (result === undefined ? (result = compute()) : result), day, short, range, scan };
scan(document);
let queued = false;
new MutationObserver((records) => {
  if (queued) return;
  for (const rec of records) {
    for (const n of rec.addedNodes) {
      if (n.nodeType === 1 && (n.matches("[data-yf-date]") || n.querySelector("[data-yf-date]"))) {
        queued = true;
        requestAnimationFrame(() => { queued = false; scan(document); });
        return;
      }
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });
